import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Group, Button, Text, Loader, Alert } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useCourse, useScormLaunch } from '../../../hooks/useApi';
import { API_URL } from '../../../utils/api';

const HEADER_HEIGHT = 48;

// SCORM API пакету даёт сам бэкенд: встраивает его в страницы пакета на своём домене.
// Отсюда до пакета не дотянуться — он на другом домене, — поэтому здесь только iframe.
export const ScormPlayerPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: course, isLoading } = useCourse(id);
  const { data: launch, isError } = useScormLaunch(id);

  // Пакет сохраняет статус сам; после выхода из плеера карточка курса должна его увидеть.
  useEffect(
    () => () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
    },
    [queryClient],
  );

  const header = (
    <Group
      px="md"
      gap="sm"
      align="center"
      style={{
        height: HEADER_HEIGHT,
        borderBottom: '1px solid var(--mantine-color-default-border)',
        flexShrink: 0,
      }}
    >
      <Button
        variant="subtle"
        size="compact-sm"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate(`/learning/${id}`)}
      >
        {t('common.back')}
      </Button>
      <Text size="sm" fw={500} truncate style={{ flex: 1 }}>
        {course?.title}
      </Text>
    </Group>
  );

  if (isError) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {header}
        <Alert color="red" m="md">
          {t('learning.scormLaunchError')}
        </Alert>
      </div>
    );
  }

  if (isLoading || !course || !launch) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <Loader size="sm" />
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {header}
      <iframe
        title={course.title}
        src={`${API_URL}${launch.url}`}
        sandbox="allow-scripts allow-same-origin allow-forms"
        style={{
          border: 'none',
          width: '100%',
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          flex: 1,
        }}
      />
    </div>
  );
};
